import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, LogOut, Loader2, X, Upload, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { value: "scented", label: "Scented" },
  { value: "floral", label: "Floral" },
  { value: "plaster", label: "Figurines" },
  { value: "kits", label: "DIY Kits" },
  { value: "decor", label: "Decor" },
];

const EMPTY = { name: "", category: "scented", price: "", description: "", image: "", featured: false };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/products")
      .then((res) => setProducts(res.data))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, category: p.category, price: p.price, description: p.description, image: p.image, featured: p.featured });
    setModalOpen(true);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB)");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post("/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const absoluteUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm((f) => ({ ...f, image: absoluteUrl }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed — please sign in again");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product added");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error("Save failed — please sign in again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Deleted");
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0C0A09] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C4A47C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0A09]" data-testid="admin-page">
      <header className="border-b border-[#292524] sticky top-0 bg-[#0C0A09]/80 backdrop-blur-xl z-40">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div>
            <p className="font-serif text-2xl text-[#F5F5F0]">Ave<span className="text-[#C4A47C]">.</span>luxe · Studio</p>
            <p className="label mt-0.5">{user.email}</p>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="inline-flex items-center gap-2 rounded-full border border-[#292524] hover:border-[#C4A47C] text-[#F5F5F0] px-5 py-2.5 text-sm transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={16} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-serif text-4xl text-[#F5F5F0] tracking-tight">Products</h1>
            <p className="label mt-2">{products.length} items in catalog</p>
          </div>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 rounded-full bg-[#C4A47C] hover:bg-[#D8BC97] text-[#0C0A09] px-6 py-3 text-sm font-medium transition-colors"
            data-testid="admin-add-btn"
          >
            <Plus size={16} strokeWidth={2} /> Add product
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#C4A47C]" /></div>
        ) : (
          <div className="border border-[#292524] rounded-sm overflow-hidden" data-testid="admin-product-table">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#292524] bg-[#1C1917]/50">
              <span className="label col-span-5">Product</span>
              <span className="label col-span-3">Category</span>
              <span className="label col-span-2">Price</span>
              <span className="label col-span-2 text-right">Actions</span>
            </div>
            {products.map((p) => (
              <div key={p.id} className="grid grid-cols-2 sm:grid-cols-12 gap-4 px-6 py-4 border-b border-[#292524] items-center" data-testid={`admin-row-${p.id}`}>
                <div className="col-span-2 sm:col-span-5 flex items-center gap-4">
                  <img src={p.image} alt={p.name} className="w-14 h-14 object-cover rounded-sm border border-[#292524]" />
                  <div className="flex items-center gap-2">
                    <span className="text-[#F5F5F0] font-light">{p.name}</span>
                    {p.featured && <Star size={13} className="fill-[#C4A47C] text-[#C4A47C]" strokeWidth={0} />}
                  </div>
                </div>
                <span className="sm:col-span-3 text-[#A8A29E] text-sm capitalize">{p.category}</span>
                <span className="sm:col-span-2 font-serif text-[#C4A47C]">{p.price}</span>
                <div className="col-span-2 sm:col-span-2 flex sm:justify-end gap-2">
                  <button onClick={() => openEdit(p)} className="p-2 rounded-full border border-[#292524] hover:border-[#C4A47C] text-[#F5F5F0] transition-colors" data-testid={`admin-edit-${p.id}`}>
                    <Pencil size={15} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(p)} className="p-2 rounded-full border border-[#292524] hover:border-[#7F1D1D] text-[#D89797] transition-colors" data-testid={`admin-delete-${p.id}`}>
                    <Trash2 size={15} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            onClick={() => setModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#1C1917] border border-[#292524] rounded-sm p-8 my-8"
              data-testid="admin-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl text-[#F5F5F0]">{editing ? "Edit product" : "New product"}</h2>
                <button onClick={() => setModalOpen(false)} className="text-[#A8A29E] hover:text-[#F5F5F0]" data-testid="admin-modal-close">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={save} className="space-y-4">
                <div>
                  <label className="label block mb-2">Name</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name"
                    className="w-full bg-[#0C0A09] border border-[#292524] focus:border-[#C4A47C] outline-none rounded-sm px-4 py-3 text-[#F5F5F0]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label block mb-2">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category"
                      className="w-full bg-[#0C0A09] border border-[#292524] focus:border-[#C4A47C] outline-none rounded-sm px-4 py-3 text-[#F5F5F0]">
                      {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label block mb-2">Price</label>
                    <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="₹499" data-testid="form-price"
                      className="w-full bg-[#0C0A09] border border-[#292524] focus:border-[#C4A47C] outline-none rounded-sm px-4 py-3 text-[#F5F5F0]" />
                  </div>
                </div>
                <div>
                  <label className="label block mb-2">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="form-description"
                    className="w-full bg-[#0C0A09] border border-[#292524] focus:border-[#C4A47C] outline-none rounded-sm px-4 py-3 text-[#F5F5F0] resize-none" />
                </div>
                <div>
                  <label className="label block mb-2">Image</label>
                  <div className="flex items-center gap-4">
                    {form.image ? (
                      <img src={form.image} alt="preview" className="w-16 h-16 object-cover rounded-sm border border-[#292524]" />
                    ) : (
                      <div className="w-16 h-16 rounded-sm border border-dashed border-[#292524] flex items-center justify-center text-[#A8A29E]">
                        <Upload size={18} strokeWidth={1.5} />
                      </div>
                    )}
                    <label className={`cursor-pointer rounded-full border px-4 py-2.5 text-sm transition-colors inline-flex items-center gap-2 ${uploading ? "border-[#292524] text-[#A8A29E] pointer-events-none" : "border-[#292524] hover:border-[#C4A47C] text-[#F5F5F0]"}`} data-testid="form-image-label">
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} strokeWidth={1.5} />}
                      {uploading ? "Uploading…" : "Upload photo"}
                      <input type="file" accept="image/*" onChange={onFile} disabled={uploading} className="hidden" data-testid="form-image-input" />
                    </label>
                  </div>
                  <input value={form.image?.startsWith("data:") ? "" : form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="…or paste an image URL"
                    className="w-full mt-3 bg-[#0C0A09] border border-[#292524] focus:border-[#C4A47C] outline-none rounded-sm px-4 py-2.5 text-sm text-[#F5F5F0]" data-testid="form-image-url" />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#C4A47C] w-4 h-4" data-testid="form-featured" />
                  <span className="text-sm text-[#F5F5F0]">Mark as bestseller</span>
                </label>

                <button type="submit" disabled={saving || uploading} data-testid="form-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C4A47C] hover:bg-[#D8BC97] disabled:opacity-60 text-[#0C0A09] px-6 py-3.5 text-sm font-medium transition-colors">
                  {saving && <Loader2 size={16} className="animate-spin" />}
                  {editing ? "Save changes" : "Add product"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
